import {
  Button,
  column,
  Css,
  GridColumn,
  GridDataRow,
  GridTable,
  ModalBody,
  ModalFooter,
  ModalHeader,
  simpleHeader,
  SimpleHeaderAndData,
  useModal,
} from "@homebound/beam";
import { Link } from "react-router-dom";
import { tagCell } from "src/components";
import { CommitmentStatus } from "src/generated/graphqlTypes";
import { createCommitmentChangeOrderUrl, createCommitmentUrl } from "src/RouteUrls";
import { commitmentStatusToTagTypeMapper, fail } from "src/utils";
import { PurchaseOrderCleanup_CommitmentLikeFragment } from "./PurchaseOrderCleanupModal.generated";

type PurchaseOrderCleanupModalProps = {
  projectId: string;
  onSaveDraftAndExit: () => Promise<void>;
  /** Only the commitments that actually need cleanup. */
  commitments: PurchaseOrderCleanup_CommitmentLikeFragment[];
};

export function PurchaseOrderCleanupModal(props: PurchaseOrderCleanupModalProps) {
  const { projectId, commitments, onSaveDraftAndExit } = props;
  const { closeModal } = useModal();

  return (
    <>
      <ModalHeader>Purchase Order Cleanup Required</ModalHeader>
      <ModalBody>
        <div css={Css.df.fdc.gap3.$}>
          <div css={Css.sm.gray900.$}>
            Some purchase orders affected by this Change Event require manual updates before it can be submitted for
            approval. After completing these actions, return here and click 'Create' to submit this Change Event for
            approval.
          </div>
          <GridTable columns={createColumns(projectId)} rows={createRows(commitments)} />
        </div>
      </ModalBody>
      <ModalFooter>
        <Button label="Cancel" variant="quaternary" onClick={() => closeModal()} />
        <Button
          label="Save Draft & Exit"
          onClick={async () => {
            closeModal();
            await onSaveDraftAndExit();
          }}
        />
      </ModalFooter>
    </>
  );
}

function purchaseOrderCleanupReason(commitmentLike: PurchaseOrderCleanup_CommitmentLikeFragment): string {
  const { status, billedInCents } = commitmentLike;
  if (status === CommitmentStatus.Voided) {
    fail("Voided commitments don't need cleanup");
  } else if (status === CommitmentStatus.Draft) {
    return "Delete this draft.";
  } else if (billedInCents) {
    // Anything billed can't be voided anymore, the money already moved.
    return "Create a manual change order.";
  } else {
    return "Void this PO. This will be replaced.";
  }
}

type Row = SimpleHeaderAndData<PurchaseOrderCleanup_CommitmentLikeFragment & { requiredAction: string }>;

function createRows(commitments: PurchaseOrderCleanup_CommitmentLikeFragment[]): GridDataRow<Row>[] {
  return [
    simpleHeader,
    ...commitments.map((c) => ({
      kind: "data" as const,
      id: c.id,
      data: { ...c, requiredAction: purchaseOrderCleanupReason(c) },
    })),
  ];
}

function createColumns(projectId: string): GridColumn<Row>[] {
  return [
    column<Row>({
      header: "Purchase Order",
      data: (c) => (
        <Link
          target="_blank"
          to={
            c.__typename === "Commitment"
              ? createCommitmentUrl(projectId, c.id)
              : createCommitmentChangeOrderUrl(projectId, c.id)
          }
        >
          {c.shortName}
        </Link>
      ),
    }),
    column<Row>({ header: "Status", data: (c) => tagCell(commitmentStatusToTagTypeMapper[c.status], c.statusText) }),
    column<Row>({ header: "Required Action", data: (c) => c.requiredAction, w: 2 }),
  ];
}
